import React, { useState } from 'react';
/**
 * Project Card - Holographic Portal Representation
 * 3D interactive project portals with magnetic hover effects
 */
import { motion, useMotionValue, useTransform } from 'framer-motion';
import { ArrowRight, Zap } from 'lucide-react';
import { useTheme } from '../../../core/contexts/ThemeContext';
import { Project } from './data/projects';
interface ProjectCardProps {
  project: Project;
  index: number;
  onClick: () => void;
  // [VERWIJDERD]: mousePosition prop is verwijderd
  isMobile: boolean;
}
export function ProjectCard({
  project,
  index,
  onClick,
  // [VERWIJDERD]: mousePosition destructurering is verwijderd
  isMobile
}: ProjectCardProps) {
  const [isHovered, setIsHovered] = useState(false);
  const {
    theme: currentTheme
  } = useTheme();
  const Icon = project.icon;
  const isLightTheme = currentTheme === 'light';
  const isNeonTheme = currentTheme === 'neon';
  const isHighContrast = currentTheme === 'highContrast';

  // [VERWIJDERD]: magneticX/Y berekeningen zijn verwijderd

  return <motion.div initial={{
    opacity: 0,
    y: 30,
    scale: 0.9
  }} animate={{
    opacity: 1,
    y: 0,
    scale: 1
  }} transition={{
    delay: index * 0.1,
    type: 'spring',
    bounce: 0.4
  }} layout data-id="element-362">
      <motion.button onClick={onClick} onHoverStart={() => !isMobile && setIsHovered(true)} onHoverEnd={() => !isMobile && setIsHovered(false)} onTouchStart={() => setIsHovered(true)} onTouchEnd={() => setTimeout(() => setIsHovered(false), 300)} whileTap={{
      scale: 0.95
    }} className="relative w-full text-left group touch-manipulation" style={{
      // [VERWIJDERD]: magnetic transform is verwijderd
      transform: 'none',
      transition: 'transform 0.3s ease-out'
    }} data-id="element-363">
        {/* Outer Glow Ring - Desktop only (Micro-animaties gereduceerd/verwijderd) */}
        {!isHighContrast && !isMobile && isHovered && <motion.div animate={{
        rotate: 360,
        // Scale pulse verwijderd, terug naar statische scale
        scale: 1.1
      }} transition={{
        rotate: {
          duration: 4,
          ease: 'linear',
          repeat: Infinity
        },
        scale: {
          duration: 0.3 // Instant scaling on hover
        }
      }} className="absolute -inset-4 rounded-3xl opacity-30" style={{
        background: `conic-gradient(from 0deg, ${project.gradient.split(' ').slice(1, 3).join(' ')}, transparent, ${project.gradient.split(' ').slice(1, 3).join(' ')})`,
        filter: 'blur(30px)'
      }} data-id="element-364" />}
        {/* Main Card Portal */}
        <motion.div animate={{
        rotateY: !isMobile && isHovered ? 8 : 0,
        rotateX: !isMobile && isHovered ? -4 : 0
      }} style={{
        transformStyle: 'preserve-3d',
        perspective: '1000px'
      }} className={`relative backdrop-blur-2xl rounded-2xl md:rounded-3xl overflow-hidden border-2 shadow-2xl ${isLightTheme ? 'bg-white/60 border-gray-200' : isHighContrast ? 'bg-gray-900 border-white' : isNeonTheme ? 'bg-black/80 border-fuchsia-500/50' : 'bg-white/5 border-white/10'}`} data-id="element-365">
          {/* Animated Gradient Background */}
          <motion.div animate={{
          opacity: isHovered ? 0.2 : 0.05,
          scale: isHovered ? 1.1 : 1
        }} transition={{
          duration: 0.4
        }} className={`absolute inset-0 bg-gradient-to-br ${project.gradient}`} style={{
          mixBlendMode: isHighContrast ? 'normal' : 'screen'
        }} data-id="element-366" />
          {/* Scan Lines Effect - Desktop only (Behouden, zorgt voor vibe) */}
          {!isHighContrast && !isMobile && isHovered && <motion.div animate={{
          y: ['0%', '100%']
        }} transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'linear'
        }} className="absolute inset-0 pointer-events-none" style={{
          background: `repeating-linear-gradient(
                  0deg,
                  transparent,
                  transparent 3px,
                  ${isNeonTheme ? 'rgba(0, 255, 255, 0.1)' : 'rgba(236, 72, 153, 0.1)'} 3px,
                  ${isNeonTheme ? 'rgba(0, 255, 255, 0.1)' : 'rgba(236, 72, 153, 0.1)'} 6px
                )`
        }} data-id="element-367" />}
          {/* Thumbnail with Parallax */}
          <div className="relative h-48 md:h-56 overflow-hidden" data-id="element-368">
            <motion.img src={project.thumbnail} alt={project.title} className={`w-full h-full object-cover ${isHighContrast ? 'grayscale' : ''}`} animate={{
            scale: isHovered ? 1.15 : 1
          }} transition={{
            duration: 0.6,
            ease: 'easeOut'
          }} style={{
            // [VERWIJDERD]: Thumbnail Parallax transform is verwijderd
            transform: 'none'
          }} data-id="element-369" />
            <div className={`absolute inset-0 ${isHighContrast ? 'bg-gradient-to-t from-gray-900 via-gray-900/60 to-transparent' : 'bg-gradient-to-t from-black/90 via-black/50 to-transparent'}`} data-id="element-370" />
            {/* Floating Icon Badge */}
            <motion.div animate={{
            y: isHovered ? -8 : 0,
            rotate: isHovered ? 360 : 0,
            scale: isHovered ? 1.15 : 1
          }} transition={{
            y: {
              duration: 0.3
            },
            rotate: {
              duration: 2,
              ease: 'linear'
            },
            scale: {
              duration: 0.3
            }
          }} className={`absolute top-4 right-4 w-12 h-12 md:w-14 md:h-14 rounded-xl backdrop-blur-xl bg-gradient-to-br ${project.gradient} flex items-center justify-center shadow-2xl ${isNeonTheme ? 'shadow-fuchsia-500/50' : ''}`} style={{
            border: isHighContrast ? '2px solid white' : '1px solid rgba(255, 255, 255, 0.2)'
          }} data-id="element-371">
              <Icon className="w-6 h-6 md:w-7 md:h-7 text-white" data-id="element-372" />
              {/* Orbiting particles - Desktop only (Behouden op hover) */}
              {!isHighContrast && !isMobile && isHovered && <>
                  {[...Array(4)].map((_, i) => <motion.div key={i} className="absolute w-1.5 h-1.5 rounded-full bg-white" style={{
                left: '50%',
                top: '50%'
              }} animate={{
                rotate: 360,
                scale: [0, 1, 0]
              }} transition={{
                rotate: {
                  duration: 2,
                  repeat: Infinity,
                  ease: 'linear'
                },
                scale: {
                  duration: 2,
                  repeat: Infinity,
                  delay: i * 2 / 4
                }
              }} style={{
                transformOrigin: `${40 + Math.cos(i / 4 * Math.PI * 2) * 30}px ${40 + Math.sin(i / 4 * Math.PI * 2) * 30}px`
              }} data-id="element-373" />)}
                </>}
            </motion.div>
            {/* Tags with glassmorphism */}
            <div className="absolute bottom-4 left-4 right-4 flex gap-2 flex-wrap" data-id="element-374">
              {project.tags.slice(0, 3).map((tag, i) => <motion.span key={tag} initial={{
              opacity: 0,
              x: -10
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: index * 0.1 + i * 0.05
            }} className={`px-3 py-1 rounded-lg backdrop-blur-xl text-xs font-medium border ${isHighContrast ? 'bg-white text-black border-white' : isNeonTheme ? 'bg-black/60 text-cyan-300 border-cyan-500/30' : 'bg-black/40 text-white/90 border-white/20'}`} data-id="element-375">
                  {tag}
                </motion.span>)}
            </div>
          </div>
          {/* Content Section */}
          <div className="relative p-5 md:p-6 space-y-3 z-10" data-id="element-376">
            <div data-id="element-377">
              {/* Micro-animatie is prima */}
              <motion.h3 animate={{
              y: isHovered ? -4 : 0
            }} className={`text-xl md:text-2xl font-bold mb-2 ${isLightTheme ? 'text-gray-900' : isHighContrast ? 'text-white' : 'text-white'}`} style={{
              textShadow: isNeonTheme && isHovered ? '0 0 20px rgba(0, 255, 255, 0.5)' : undefined
            }} data-id="element-378">
                {project.title}
              </motion.h3>
              <p className={`text-sm md:text-base line-clamp-2 ${isLightTheme ? 'text-gray-600' : isHighContrast ? 'text-gray-300' : isNeonTheme ? 'text-cyan-300' : 'text-white/70'}`} data-id="element-379">
                {project.subtitle}
              </p>
            </div>
            {/* Hover Description - Desktop only */}
            {!isMobile && <motion.div initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: isHovered ? 1 : 0,
            height: isHovered ? 'auto' : 0
          }} className="overflow-hidden" data-id="element-380">
                <p className={`text-sm leading-relaxed ${isLightTheme ? 'text-gray-700' : isHighContrast ? 'text-gray-200' : isNeonTheme ? 'text-cyan-200' : 'text-white/70'}`} data-id="element-381">
                  {project.story.intentie.description.slice(0, 120)}...
                </p>
              </motion.div>}
            {/* CTA with energy effect */}
            <motion.div animate={{
            x: isHovered ? 6 : 0
          }} className="flex items-center gap-2 pt-2" data-id="element-382">
              <Zap className={`w-4 h-4 ${isHighContrast ? 'text-white' : isNeonTheme ? 'text-cyan-400' : 'text-fuchsia-400'}`} data-id="element-383" />
              <span className={`text-sm font-medium ${isHighContrast ? 'text-white' : isNeonTheme ? 'text-cyan-400' : 'text-fuchsia-400'}`} data-id="element-384">
                Verken project
              </span>
              <ArrowRight className={`w-4 h-4 ${isHighContrast ? 'text-white' : isNeonTheme ? 'text-cyan-400' : 'text-fuchsia-400'}`} data-id="element-385" />
            </motion.div>
          </div>
          {/* Corner Accents - Desktop only */}
          {!isHighContrast && !isMobile && isHovered && <>
              {['top-0 left-0', 'top-0 right-0', 'bottom-0 left-0', 'bottom-0 right-0'].map((position, i) => <motion.div key={i} initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} className={`absolute ${position} w-8 h-8`} style={{
            background: `linear-gradient(${i % 2 === 0 ? '135deg' : '45deg'}, ${isNeonTheme ? '#00ffff' : '#ec4899'}, transparent)`
          }} data-id="element-386" />)}
            </>}
        </motion.div>
        {/* Holographic Glow - Desktop only */}
        {isHovered && !isHighContrast && !isMobile && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} className={`absolute -inset-6 bg-gradient-to-br ${project.gradient} blur-3xl -z-10 rounded-3xl ${isLightTheme ? 'opacity-20' : isNeonTheme ? 'opacity-40' : 'opacity-30'}`} data-id="element-387" />}
      </motion.button>
    </motion.div>;
}